/**
 * MIT License
 *
 * Copyright (c) 2023 Jamasp Team <mailto:radmanesh@gmail.com Arman Radmanesh> <mailto:salarirad@gmail.com Salar Rad>
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import { sendSignInLinkToEmail, signInWithPopup } from 'firebase/auth';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import PropTypes from 'prop-types';
import { createContext, useContext, useEffect, useState } from 'react';
import { useAlert } from '../context/AlertContext';
import { auth, db, googleProvider } from '../firebase';
import { getCurrentUserDocument, getUserDocument } from './AuthUtils';

// Create a context
export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userClaims, setUserClaims] = useState(null);
  const [loading, setLoading] = useState(true); // Add a loading state
  //const [phoneVerified, setPhoneVerified] = useState(false); // Add a phoneVerified state
  //const [phoneNumber, setPhoneNumber] = useState(null); // Add a phone number state
  const [userJamasp, setUserJamasp] = useState(null); // Add a user in jamasp state

  const { addAlert } = useAlert();

  useEffect(() => {
    // TODO: Check the this logic completely, this one runs only once and if userDoc doesn't exist it will throws an error
    // This is a good method for checking user has validated phone number, but not in registration process

    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setLoading(true); // Set loading to true when user is fetched
      console.log("auth.onAuthStateChanged", user, user?.isNewUser);
      setCurrentUser(user);  // Synchronous operation
      if (user && user?.uid) {
        try {
          // Roles come from the ID token's custom claims (set server-side), not from Firestore.
          const tokenResult = await user.getIdTokenResult();
          setUserClaims({
            admin: tokenResult?.claims?.admin === true,
            role: tokenResult?.claims?.role || null,
          });
          const userDoc = await getCurrentUserDocument();
          if (userDoc !== null && userDoc?.data()) {
            setUserJamasp(userDoc.data());
            //setPhoneVerified(userDoc.data()?.phoneNumberVerified);
            //setPhoneNumber(userDoc.data()?.phoneNumber || userDoc.data()?.phone);
          }
        } catch (err) {
          console.log("Error in getting user data, maybe its registering or trying to get user data", err);
          addAlert("error", "Error in getting user data. Please try again. Or contact support.");
        } finally {
          setLoading(false); // Set loading to false once user is fetched
        }
      } else {
        setUserClaims(null);
        setLoading(false); // Set loading to false when no user is logged in
      }
    });

    return () => unsubscribe();
  }, [addAlert]);

  // TODO: sign in and sign up should be different in a way that sign up should check if the user is already signed up or not
  // now they are the same.
  const signInWithGoogle = async () => {
    try {
      const res = await signInWithPopup(auth, googleProvider);
      const user = res.user;
      const userDoc = await getUserDocument(user.uid);
      if (userDoc === null) {
        await setDoc(doc(db, "users", user.uid), {
          uid: user.uid,
          name: user.displayName,
          authProvider: "google",
          email: user.email,
          photoURL: user.photoURL,
          createdAt: serverTimestamp(),
        }).catch((err) => {
          console.error(err);
          addAlert("error", "Error in creating user. Please try again. Or contact support.");
        });
      }
      return user;
    } catch (err) {
      console.error(err);
      addAlert("error", "Error in fetching the user. Please try again. Or contact support.");
    }
  };

  // TODO: sign in and sign up should be different in a way that sign up should check if the user is already signed up or not
  // now they are the same.
  const signUpWithGoogle = async () => {
    try {
      const res = await signInWithPopup(auth, googleProvider);
      const user = res.user;

      const userDoc = await getUserDocument(user.uid).catch((err) => {
        console.log("can't get user doc, maybe registering", err);
        return null;
      });
      if (userDoc === null) {
        await setDoc(doc(db, "users", user.uid), {
          uid: user.uid,
          name: user.displayName,
          authProvider: "google",
          email: user.email,
          photoURL: user.photoURL,
          createdAt: serverTimestamp(),
        }).catch((err) => {
          console.error(err);
          addAlert("error", "Error in creating user. Please try again. Or contact support.");
        });
      }
      return user;
    } catch (err) {
      console.error(err);
      addAlert("error", "Error in creating user. Please try again. Or contact support.");
      // TODO: setup alerts
      return null;
    }
  }

  const logout = async (callback) => {
    try {
      await auth.signOut();
      console.log("Sign-out successful", callback, currentUser);
      setCurrentUser(null);
      addAlert("success", "Sign-out successful");
      if (callback) {
        callback();
      }
      // navigate to home page
      // navigate('/', { replace: true });
    } catch (err) {
      console.error(err);
      addAlert("error", "Error in logging out. Please try again. Or contact support.");
    }
  };

  const signUpWithEmailLink = async (email, actionCodeSettings) => {
    console.log("signUpWithEmailLink", email, actionCodeSettings);
    sendSignInLinkToEmail(auth, email, actionCodeSettings)
      .then(() => {
        // The link was successfully sent. Inform the user.
        // Save the email locally so you don't need to ask the user for it again
        // if they open the link on the same device.
        window.localStorage.setItem('emailForSignIn', email);
        // ...
        return { success: true };
      })
      .catch((error) => {
        const errorCode = error.code;
        const errorMessage = error.message;
        console.log(errorCode);
        console.log(errorMessage);
        return { success: false, errorCode: errorCode, errorMessage: errorMessage };
        // ...
      });
  };

  // TODO: this function should be moved to another file
  // TODO: user claim is mocked
  function hasRole(role) {
    // mock userClaims
    if (role === "user")
      return true;
    if (!userClaims) {
      console.log("hasRole, No user claims, returning false, role: ", role);
      return false;
    }
    if (role === "admin")
      return userClaims.admin===true;
    if (!userClaims?.role)
      return false;
    return userClaims.role === role;
    //return true;
  }

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        userJamasp,
        signInWithGoogle,
        signUpWithGoogle,
        logout,
        signUpWithEmailLink,
        loading,
        hasRole,
        userClaims
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuthContext = () => {
  return useContext(AuthContext);
};
